import React, { useState, useEffect, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { IMonthlyNotesTotal } from '@/features/calendar/hooks/useNotes';

interface INotesMonthSliderProps {
  selectedMonth: number;
  selectedYear: number;
  onMonthSelect: (month: number, year: number) => void;
  monthlyTotals: IMonthlyNotesTotal[];
}

type Month = {
  month: number;
  year: number;
};

export const NotesMonthSlider: React.FC<INotesMonthSliderProps> = ({ 
  selectedMonth, 
  selectedYear, 
  onMonthSelect, 
  monthlyTotals 
}) => {
  const { t } = useI18n();
  const [visibleMonths, setVisibleMonths] = useState<Month[]>([]);
  
  const monthNames = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];

  useEffect(() => {
    // Generate visible months (current year and adjacent years)
    const months = [];
    const currentYear = new Date().getFullYear();
    
    // Add previous year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear - 1 });
    }
    
    // Add current year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear });
    }
    
    // Add next year months
    for (let month = 1; month <= 12; month++) {
      months.push({ month, year: currentYear + 1 });
    }
    
    setVisibleMonths(months);
  }, []);

  const getNotesCount = (month: number, year: number): number => {
    // Find matching month in totals - simple and direct lookup
    const monthData = monthlyTotals.find(m => m.month === month && m.year === year);
    return monthData?.notes_count || 0;
  };

  const scrollToSelectedMonth = useCallback(() => {
    const selectedIndex = visibleMonths.findIndex(
      m => m.month === selectedMonth && m.year === selectedYear
    );
    
    if (selectedIndex >= 0) {
      const container = document.getElementById('notes-month-slider-container');
      const monthItem = document.getElementById(`notes-month-item-${selectedIndex}`);
      
      if (container && monthItem) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = monthItem.getBoundingClientRect();
        const scrollLeft = container.scrollLeft + itemRect.left - containerRect.left - (containerRect.width / 2) + (itemRect.width / 2);
        
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedMonth, selectedYear, visibleMonths]);

  useEffect(() => {
    scrollToSelectedMonth();
  }, [scrollToSelectedMonth]);

  return (
    <div className="w-full mb-4">
      <div 
        id="notes-month-slider-container"
        data-testid="notes-month-slider-container"
        className="flex gap-3 overflow-x-auto scrollbar-hide px-2 py-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {visibleMonths.map(({ month, year }, index) => {
          const isSelected = month === selectedMonth && year === selectedYear;
          const notesCount = getNotesCount(month, year);
          
          return (
            <div
              key={`${year}-${month}`}
              id={`notes-month-item-${index}`}
              className={`flex flex-col items-center min-w-[50px] cursor-pointer transition-all duration-200 ${
                isSelected ? 'scale-105' : 'hover:scale-102'
              }`}
              onClick={() => onMonthSelect(month, year)}
            >
              {/* Month initials */}
              <span className={`text-xs font-medium ${
                isSelected ? 'text-white' : 'text-white/60'
              }`}>
                {t(`calendar.months.${monthNames[month - 1]}`)}
              </span>
              
              {/* Notes count with icon */}
              <div className={`flex items-center gap-1 ${
                isSelected ? 'text-white' : 'text-white/40'
              }`}>
                <span className="text-[10px] font-medium">
                  {notesCount.toString().padStart(2, '0')}
                </span>
                <Calendar className="w-2.5 h-2.5" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

